import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { Button } from '../components/ui';

/**
 * Maps one unmatched import row to a product by hand.
 *
 * Opens pre-filled with what the merchant wrote on that row, and offers the
 * shortlist we already worked out before making them search — most of the time
 * the right answer is one of two or three candidates and this is a single click.
 *
 * Searching uses the same POST /api/products/search the rest of the product
 * screens use, with the same filters (brand, model, reference, dial colour,
 * case size), so the fields here behave exactly as they do elsewhere.
 */

const FILTERS = [
  { key: 'reference_number', label: 'Reference', from: 'reference' },
  { key: 'query', label: 'Brand or model', from: 'brand' },
  { key: 'dial_color', label: 'Dial colour', from: 'dial_colour', nested: true },
  { key: 'case_diameter', label: 'Case size', from: 'case_size', nested: true },
];

function describe(product) {
  return [
    product.brand?.name || product.brand,
    product.model?.name || product.model,
    product.reference_number,
  ].filter(Boolean).join(' · ');
}

function attributes(product) {
  return [product.dial_color, product.color, product.case_size]
    .filter(Boolean)
    .join(' · ');
}

export function ProductPicker({ row, onPick, onClose }) {
  const [values, setValues] = useState(() => {
    const seed = {};
    FILTERS.forEach((f) => { seed[f.key] = row.row?.[f.from] || ''; });
    return seed;
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = useCallback(async () => {
    setLoading(true);
    setSearched(true);

    const filters = { values: {} };
    FILTERS.forEach((f) => {
      const value = values[f.key]?.trim();
      if (!value || f.key === 'query') return;
      if (f.nested) filters.values[f.key] = value;
      else filters[f.key] = value;
    });

    try {
      const { data } = await axios.post('/api/products/search', {
        list: 'default',
        query: values.query || '',
        filters,
        paging: { offset: 0, limit: 25 },
      });
      setItems(data?.items ?? []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [values]);

  // Search once on open using the row's own values — the merchant shouldn't
  // have to press anything to see the obvious candidates.
  useEffect(() => { search(); }, []);

  const shortlist = row.candidates || [];

  return (
    <div className="rc-picker" role="dialog" aria-label={`Choose a product for row ${row.line}`}>
      <div className="rc-picker__panel">
        <header className="rc-picker__head">
          <div>
            <h2 className="rc-picker__title">Which watch is this?</h2>
            <p className="rc-picker__sub">Row {row.line} — {row.summary || 'your sheet'}</p>
          </div>
          <button type="button" className="rc-picker__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {shortlist.length ? (
          <section className="rc-picker__section">
            <h3 className="rc-picker__sectiontitle">Closest matches from your row</h3>
            <ul className="rc-picker__list">
              {shortlist.map((product) => (
                <li key={product.id} className="rc-picker__item">
                  <span className="rc-picker__itemtext">
                    <span className="rc-picker__itemname">{describe(product)}</span>
                    <span className="rc-picker__itemattrs">{attributes(product) || 'No further details'}</span>
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => onPick(row.line, product.id)}>
                    Use this
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section className="rc-picker__section">
          <h3 className="rc-picker__sectiontitle">Search the catalogue</h3>
          <div className="rc-picker__filters">
            {FILTERS.map((f) => (
              <label key={f.key} className="rc-picker__field">
                <span className="rc-picker__fieldlabel">{f.label}</span>
                <input
                  type="text"
                  className="rc-picker__input"
                  value={values[f.key]}
                  onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                />
              </label>
            ))}
          </div>
          <Button variant="primary" size="lg" onClick={search} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </Button>

          {searched && !loading && items.length === 0 ? (
            <p className="rc-picker__empty">
              Nothing matched. Try just the reference, or widen the search by clearing a field.
            </p>
          ) : null}

          {items.length ? (
            <ul className="rc-picker__list">
              {items.map((product) => (
                <li key={product.id} className="rc-picker__item">
                  <span className="rc-picker__itemtext">
                    <span className="rc-picker__itemname">{describe(product)}</span>
                    <span className="rc-picker__itemattrs">{attributes(product) || 'No further details'}</span>
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => onPick(row.line, product.id)}>
                    Use this
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </div>
  );
}

export default ProductPicker;
